/**
 * Week timeline view model — pure derivation.
 *
 * The week by line drawn on one continuous time axis: a step (batch) is one
 * card from its start to its end, crossing midnight, and its people sit inside
 * on their own hours. Built by stitching the per-day lanes of `buildWeekLanes`
 * back together: the same step, phase, card or open slot on consecutive days
 * becomes one piece. Times are decimal hours from Monday 00:00 of the week.
 * Nothing here touches React or the server.
 */
import { groupCrewLines, LaneCard, LaneCrewGroup, LanePhase, LaneSlot, LaneStep } from "./lane-rows"; // eslint-disable-line sort-imports
import { WeekDayHeader, WeekLanes } from "./week-lanes";

export const WEEK_HOURS = 168;

export interface TimelineStep extends Omit<LaneStep, "phases"> {
	/** yyyy-MM-dd of the first day the step runs on. */
	dayIso: string;
	phases: LanePhase[];
	groups: LaneCrewGroup[];
}

export interface TimelineLane {
	workcenter: string;
	weekHours: number;
	idle: boolean;
	steps: TimelineStep[];
}

export interface Timeline {
	lanes: TimelineLane[];
	days: WeekDayHeader[];
}

const phaseKey = (phase: LanePhase): string => (phase.phaseId === null ? "other" : String(phase.phaseId));

const shiftCard = (card: LaneCard, offset: number): LaneCard => ({ ...card, start: card.start + offset, end: card.end + offset });
const shiftSlot = (slot: LaneSlot, offset: number): LaneSlot => ({ ...slot, start: slot.start + offset, end: slot.end + offset });

/** Join a piece seen on another day onto what is already known (same id): the union of their windows. */
const joinWindow = <T extends { start: number; end: number }>(known: T, next: T): T => ({
	...known,
	start: Math.min(known.start, next.start),
	end: Math.max(known.end, next.end),
});

function mergePhase(known: LanePhase, next: LanePhase): LanePhase {
	const cards = new Map(known.cards.map((card) => [card.id, card]));
	next.cards.forEach((card) => {
		const seen = cards.get(card.id);
		cards.set(card.id, seen ? joinWindow(seen, card) : card);
	});
	const slots = new Map(known.slots.map((slot) => [`${slot.phaseId}|${slot.startIso}`, slot]));
	next.slots.forEach((slot) => {
		const key = `${slot.phaseId}|${slot.startIso}`;
		const seen = slots.get(key);
		slots.set(key, seen ? joinWindow(seen, slot) : slot);
	});
	const merged = Array.from(cards.values());
	return {
		...known,
		start: Math.min(known.start, next.start),
		end: Math.max(known.end, next.end),
		needed: Math.max(known.needed, next.needed),
		cards: merged,
		slots: Array.from(slots.values()).sort((a, b) => a.start - b.start),
		emptySlots: slots.size,
		outCount: new Set(merged.filter((card) => card.isOut).map((card) => card.workerId)).size,
	};
}

/** The week's lanes with each step and each person's block joined across midnight. */
export function buildTimeline(week: WeekLanes): Timeline {
	const lanes = week.lanes.map<TimelineLane>((lane) => {
		const steps = new Map<number, TimelineStep>();
		lane.days.forEach((day, d) => {
			const offset = d * 24;
			day.steps.forEach((step) => {
				const phases = step.phases.map<LanePhase>((phase) => ({
					...phase,
					start: phase.start + offset,
					end: phase.end + offset,
					cards: phase.cards.map((card) => shiftCard(card, offset)),
					slots: phase.slots.map((slot) => shiftSlot(slot, offset)),
				}));
				const seen = steps.get(step.workOrderId);
				if (!seen) {
					steps.set(step.workOrderId, { ...step, start: step.start + offset, end: step.end + offset, dayIso: day.dayIso, phases, groups: [] });
					return;
				}
				const byKey = new Map(seen.phases.map((phase) => [phaseKey(phase), phase]));
				phases.forEach((phase) => {
					const known = byKey.get(phaseKey(phase));
					byKey.set(phaseKey(phase), known ? mergePhase(known, phase) : phase);
				});
				steps.set(step.workOrderId, {
					...seen,
					start: Math.min(seen.start, step.start + offset),
					end: Math.max(seen.end, step.end + offset),
					crewHave: Math.max(seen.crewHave, step.crewHave),
					crewNeeded: Math.max(seen.crewNeeded, step.crewNeeded),
					crewOut: Math.max(seen.crewOut, step.crewOut),
					phases: Array.from(byKey.values()),
				});
			});
		});
		return {
			workcenter: lane.workcenter,
			weekHours: lane.weekHours,
			idle: lane.idle,
			steps: Array.from(steps.values())
				.map((step) => {
					// A card covers its whole crew: a QA check can run past the step's planned end.
					const spans = step.phases.flatMap((phase) => [phase, ...phase.cards, ...phase.slots]);
					const start = Math.min(step.start, ...spans.map((x) => x.start));
					const end = Math.max(step.end, ...spans.map((x) => x.end));
					return { ...step, start, end, groups: groupCrewLines(step.phases) };
				})
				.sort((a, b) => a.start - b.start),
		};
	});
	return { lanes, days: week.days };
}

// ---------------------------------------------------------------------------
// Rows inside a crew line: people and open slots that overlap in time stack.

export interface Packed<T> {
	item: T;
	row: number;
}

/** Greedy interval packing: each piece goes on the first row it does not overlap. */
export function packRows<T extends { start: number; end: number }>(items: T[]): { rows: number; placed: Packed<T>[] } {
	const ends: number[] = [];
	const placed = [...items]
		.sort((a, b) => a.start - b.start || a.end - b.end)
		.map((item) => {
			let row = ends.findIndex((end) => end <= item.start + 1e-6);
			if (row === -1) {
				row = ends.length;
				ends.push(item.end);
			} else {
				ends[row] = item.end;
			}
			return { item, row };
		});
	return { rows: Math.max(1, ends.length), placed };
}

// ---------------------------------------------------------------------------
// The non-uniform time axis. Each hour slot (from the day start) has a weight:
// focusing a card stretches its hours and squeezes the rest so the week stays
// on screen; hiding idle hours shrinks hours no line works.

export interface AxisOptions {
	/** Pixels per normal day. */
	dayWidth: number;
	/** Hour the production day starts (0 = midnight, 7 = 7 am). */
	dayStart: number;
	/** Hours with no step on any line shrink to a quarter. */
	hideIdle?: boolean;
	/** A window (hours from Monday 00:00) to widen until it is at least `focusWidth` pixels. */
	focus?: { start: number; end: number } | null;
	focusWidth?: number;
	/** Every step's window, for idle detection. */
	busy?: Array<{ start: number; end: number }>;
}

export interface Axis {
	/** Hours from Monday 00:00 → pixels from the axis start. */
	x: (hours: number) => number;
	/** Pixel width of [a, b). */
	w: (a: number, b: number) => number;
	width: number;
	dayStart: number;
}

export function buildAxis(options: AxisOptions): Axis {
	const { dayWidth, dayStart, hideIdle = false, focus = null, focusWidth = 420, busy = [] } = options;
	const unit = dayWidth / 24;
	let weights = new Array<number>(WEEK_HOURS).fill(1);
	if (hideIdle) {
		const used = new Array<boolean>(WEEK_HOURS).fill(false);
		busy.forEach((span) => {
			for (let h = Math.max(0, Math.floor(span.start - dayStart)); h < Math.min(WEEK_HOURS, Math.ceil(span.end - dayStart)); h++) {
				used[h] = true;
			}
		});
		weights = weights.map((weight, h) => (used[h] ? weight : 0.25));
	}
	if (focus) {
		const a = Math.max(0, Math.floor(focus.start - dayStart));
		const b = Math.min(WEEK_HOURS, Math.ceil(focus.end - dayStart));
		const inside = weights.slice(a, b).reduce((t, x) => t + x, 0);
		const total = weights.reduce((t, x) => t + x, 0);
		const grow = Math.max(1, focusWidth / Math.max(inside * unit, 1));
		const outside = total - inside;
		const shrink = outside > 0 ? Math.max(0.3, (total - inside * grow) / outside) : 1;
		weights = weights.map((weight, h) => (h >= a && h < b ? weight * grow : weight * shrink));
	}
	const cumulative = [0];
	weights.forEach((weight, h) => cumulative.push(cumulative[h] + weight));
	const x = (hours: number): number => {
		const at = Math.max(0, Math.min(WEEK_HOURS, hours - dayStart));
		const whole = Math.floor(at);
		return (cumulative[whole] + (whole < WEEK_HOURS ? (at - whole) * weights[whole] : 0)) * unit;
	};
	return { x, w: (a, b) => x(b) - x(a), width: cumulative[WEEK_HOURS] * unit, dayStart };
}

// ---------------------------------------------------------------------------
// Short codes for narrow cards. In time these come from master data (a short
// code on each product and job role); until then a few known names, else the
// initials of the name.

const PRODUCT_CODES: Record<string, string> = {
	"metoprolol beads": "MP",
	"mixed amphet. salts caps.cii-dscoat.": "MSA",
	"mixed amphet. salts caps. cii-dscoat.": "MSA",
	"type-a clean": "CLN-A",
	hold: "HLD",
	swab: "SWB",
	changeover: "CO",
	co: "CO",
};

const ROLE_CODES: Record<string, string> = {
	coating: "Ctg",
	"qa verification": "QAV",
	"drug layering": "DL",
	compression: "Cmp",
	encapsulation: "Enc",
	granulation: "Gran",
	blending: "Blnd",
	hold: "Hld",
	"type-a clean": "Cln",
	swab: "Swb",
	changeover: "CO",
};

const initialsOf = (name: string, max: number): string =>
	name
		.split(/[\s./-]+/)
		.filter((word) => /^[A-Za-z]/.test(word))
		.map((word) => word[0])
		.join("")
		.slice(0, max)
		.toUpperCase();

export const productCode = (name: string): string => PRODUCT_CODES[name.trim().toLowerCase()] ?? (initialsOf(name, 3) || name.slice(0, 3));

export const roleCode = (name: string): string => {
	const known = ROLE_CODES[name.trim().toLowerCase()];
	if (known) {
		return known;
	}
	const words = name.split(/\s+/).filter(Boolean);
	return words.length > 1 ? initialsOf(name, 3) : name.slice(0, 3);
};

/** A person's initials: first and last name. */
export const personInitials = (name: string): string => {
	const words = name.split(/\s+/).filter(Boolean);
	if (words.length === 0) {
		return "?";
	}
	return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase();
};

/** Day shifts (1st, 12h day) get a sun, evening and night shifts a moon. */
export const isDayShift = (label: string): boolean => /1st|day|first|morning/i.test(label) && !/night/i.test(label);
